import * as React from 'react';
import { Table } from '@tanstack/react-table';
import { AdjustmentsHorizontalIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { cn } from '@dashboard/lib/utils';
import { Button } from '@dashboard/components/ui/button';
import { Input } from '@dashboard/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';

// Prefer the column's string header; fall back to formatting the column id
// (camelCase/snake_case → Sentence case).
function getColumnLabel(id: string, header: unknown): string {
  if (typeof header === 'string' && header.length > 0) return header;
  const spaced = id
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

interface DataGridToolbarProps<TData> {
  table: Table<TData>;
  title?: React.ReactNode;
  showSearch?: boolean;
  showColumnToggle?: boolean;
  searchPlaceholder?: string;
  leftActions?: React.ReactNode;
  customActions?: React.ReactNode;
  /**
   * Contextual bar rendered above the main toolbar row (between the
   * heading and the search input). Used for selection-based bulk
   * actions: when rows are ticked, callers pass the action buttons
   * here so they don't fight for space with the search input.
   */
  selectionBar?: React.ReactNode;
  className?: string;
}

export function DataGridToolbar<TData>({
  table,
  title,
  showSearch = false,
  showColumnToggle = false,
  searchPlaceholder = 'Search...',
  leftActions,
  customActions,
  selectionBar,
  className,
}: DataGridToolbarProps<TData>) {
  const hasAnyTool = showSearch || showColumnToggle || leftActions || customActions || selectionBar;

  if (!hasAnyTool) {
    return null;
  }

  return (
    <div className={cn('pb-4 space-y-2', className)}>
      {selectionBar && (
        <div className="flex items-center justify-between rounded-md border border-border bg-muted/40 px-3 py-2">
          {selectionBar}
        </div>
      )}
      <div
        className={cn(
          'flex flex-wrap items-center justify-between',
          title ? 'gap-x-4 gap-y-2' : 'gap-2',
        )}
      >
        {title}
        <div className={cn('flex flex-wrap items-center gap-2', title ? 'lg:ml-auto' : 'flex-1')}>
          {showSearch && (
            <div className="relative w-[150px] lg:w-[250px]">
              <Input
                placeholder={searchPlaceholder}
                value={(table.getState().globalFilter as string) ?? ''}
                onChange={event => table.setGlobalFilter(event.target.value)}
                className="h-8 pr-7"
              />
              {((table.getState().globalFilter as string) ?? '').length > 0 && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => table.setGlobalFilter('')}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex size-5 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <XMarkIcon className="size-3.5" />
                </button>
              )}
            </div>
          )}
          {leftActions}
        </div>
        <div className={cn('flex flex-wrap items-center gap-2', !title && 'max-md:w-full')}>
          {customActions}
          {showColumnToggle && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8">
                  <AdjustmentsHorizontalIcon className="mr-2 h-4 w-4" />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[180px]">
                <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {(() => {
                  const hideableColumns = table
                    .getAllColumns()
                    .filter(
                      column => typeof column.accessorFn !== 'undefined' && column.getCanHide(),
                    );
                  const visibleColumnCount = hideableColumns.filter(col =>
                    col.getIsVisible(),
                  ).length;

                  return hideableColumns.map(column => {
                    // Prevent hiding the last visible column
                    const isLastVisibleColumn = visibleColumnCount === 1 && column.getIsVisible();

                    return (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        checked={column.getIsVisible()}
                        disabled={isLastVisibleColumn}
                        onCheckedChange={value => column.toggleVisibility(!!value)}
                      >
                        {getColumnLabel(column.id, column.columnDef.header)}
                      </DropdownMenuCheckboxItem>
                    );
                  });
                })()}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </div>
  );
}
